import cx from "clsx";
import CircularProgress$Indeterminate from "kreate-common/modules/kreate-ui/components/CircularProgress/components/CircularProgress$Indeterminate";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import { GetArtworkList$Params } from "kreate-server/commands/GetArtworkList/typing";
import * as React from "react";

import styles from "./index.module.scss";

import { ArtworkWindowContext } from "@/modules/artwork-window/contexts";
import { useIsGonnaVisible } from "@/modules/common-hooks/useIsGonnaVisible";
import CardArtwork from "@/modules/kreate-components/components/CardArtwork";
import ErrorViewer from "@/modules/kreate-components/components/ErrorViewer";
import { useArtworkList } from "@/modules/resources/hooks/useArtworkList";

type LoadEvent = {
  maybeHasMore: boolean;
  totalNumArtworks: number | undefined;
};

type Props = {
  className?: string;
  style?: React.CSSProperties;
  params?: GetArtworkList$Params;
  onLoad?: (event: LoadEvent) => void;
};

export default function LazyArtworkList$Profile({
  className,
  style,
  params,
  onLoad,
}: Props) {
  const [containerRef, setContainerRef] = React.useState<HTMLDivElement | null>(
    null
  );

  const [enabled, setEnabled] = React.useState(false);

  const isGonnaVisible = useIsGonnaVisible({
    element: containerRef,
    rootMargin: "20%",
    disabled: enabled,
  });

  const newEnabled = enabled || isGonnaVisible;
  React.useEffect(() => {
    setEnabled(newEnabled);
  }, [newEnabled]);

  const [artworksData, artworksError] = useArtworkList(
    enabled ? params : undefined
  );

  const artworksLength = artworksData?.artworks.length;

  React.useEffect(() => {
    if (artworksLength == null) return;
    onLoad?.({
      maybeHasMore: artworksLength === params?.limit,
      totalNumArtworks: artworksData?.count,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artworksLength, params?.limit]);

  return (
    <ArtworkWindowContext.Provider
      value={params ? { type: "artwork-list", fetchParams: params } : undefined}
    >
      <div
        ref={setContainerRef}
        className={cx(styles.container, className)}
        style={style}
      >
        {artworksError ? (
          <ErrorViewer
            className={className}
            style={style}
            error={artworksError}
          />
        ) : !artworksData ? (
          <Flex.Row
            className={styles.loading}
            alignItems="center"
            justifyContent="center"
            gap="16px"
          >
            <CircularProgress$Indeterminate size="24px" />
            <span>{"Loading..."}</span>
          </Flex.Row>
        ) : artworksData.count === 0 ? (
          <div className={styles.text}>No results found.</div>
        ) : (
          <div className={styles.grid}>
            {artworksData.artworks.map((artwork) => (
              <CardArtwork
                key={artwork.artworkId}
                artwork={artwork}
                sizes="660px"
                mustShowCollection={true}
              />
            ))}
          </div>
        )}
      </div>
    </ArtworkWindowContext.Provider>
  );
}
